import { ACTION_TYPES, createAction } from "@Utils/constants/actions";
import { useDispatch } from "react-redux";
import {
    ISystemLocalePayload,
    getBrowserTimezone,
} from "@Utils/getBrowserTimezone";
import {
    getLocalStorageItem,
    removeLocalStorageItem,
} from "repoV2/utils/common/storage/getterAndSetters";
import { addWeeks, isBefore } from "date-fns";
import { parseDateTime } from "@Utils/parseDateTime";
import { getIsKnownTimezone } from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { getCountryDataFromTimezone } from "@Utils/time";
import { LOCAL_STORAGE_KEYS } from "../../../repoV2/constants/common/storage/localStorage";

export const useOnIpGeolocResolutionInLegacyCode = () => {
    const dispatch = useDispatch();

    /**
     * Ip resolution api calls are now needed outside legacy code. So moved them from saga to this file.
     * This funciton is picked from the legacy code in src/redux/sagas/utils.ts file with little modifications:
     * - LEGACY code triggered the api call in this block itself.\
     *   Redux Action `ACTION_TYPES.UTILS.API_CALL` was triggered with `apiActionType === API_ACTION_TYPES.FETCH_USER_IP`.\
     *   This in turn called the `ACTION_TYPES.HOST.UPDATE_USER_IP` on IP resolution.
     * - NEW code triggers the api calls from outside this function and then calls this block on IP resolution.\
     *   So we just need to call the success action (`ACTION_TYPES.HOST.UPDATE_USER_IP`) in the end.
     */
    const onIpGeolocResolutionInLegacyCode = ({
        ip,
        countryCode,
    }: {
        ip: string;
        countryCode: string;
    }) => {
        // 1. Try to get the cached locale data
        const cachedSystemLocaleData: ISystemLocalePayload & {
            created_at?: number;
        } = getLocalStorageItem(LOCAL_STORAGE_KEYS.SYSTEM_LOCALE);

        const browserTimezone: string = getBrowserTimezone(false);

        // If the data is more than a week old, discard it
        const isCachedLocaleDataExpired = cachedSystemLocaleData?.created_at
            ? isBefore(
                  addWeeks(cachedSystemLocaleData.created_at, 1),
                  parseDateTime()
              )
            : true;

        if (isCachedLocaleDataExpired) {
            removeLocalStorageItem(LOCAL_STORAGE_KEYS.SYSTEM_LOCALE);
        }

        // Since cachedSystemLocaleData will be used below for dispatching actions, it is best to delete the created_at key from it here
        if (cachedSystemLocaleData?.created_at) {
            delete cachedSystemLocaleData.created_at;
        }

        // Note: getIsKnownTimezone can be a computationally heavy call since it needs to scan an entire list, so calls to it must be kept to a minimum
        const useCachedTimezone: boolean = !!(
            cachedSystemLocaleData?.timezone &&
            !isCachedLocaleDataExpired &&
            getIsKnownTimezone(cachedSystemLocaleData?.timezone)
        );
        const useBrowserTimezone: boolean = getIsKnownTimezone(browserTimezone);

        let systemLocalPayload: ISystemLocalePayload = cachedSystemLocaleData;

        // 2. If cached data is not to be used but browser data is to be used
        if (!useCachedTimezone && useBrowserTimezone) {
            const { countryCode: countryCodeInBrowserTimezone, countryName } =
                getCountryDataFromTimezone(browserTimezone);

            systemLocalPayload = {
                timezone: browserTimezone,
                countryCode: countryCodeInBrowserTimezone,
                countryName,
            };
        }

        // If either 1. or 2. are applicable, update the timezone in the store and make an API call to fetch the IP address without any further timezone related callbacks
        if (useCachedTimezone || useBrowserTimezone) {
            // Some API data is re-fetched once the user locale is resolved via the UPDATE_USER_LOCALE
            dispatch(
                createAction(
                    ACTION_TYPES.HOST.UPDATE_USER_LOCALE,
                    systemLocalPayload
                )
            );

            dispatch(
                createAction(ACTION_TYPES.HOST.UPDATE_USER_IP, {
                    countryCode,
                    ip,
                    metadata: {
                        getTzFromCountryCode: false,
                    },
                })
            );
        } else {
            // 3. Try to determine the locale information from the country code from the IP checking request
            dispatch(
                createAction(ACTION_TYPES.HOST.UPDATE_USER_IP, {
                    countryCode,
                    ip,
                    metadata: {
                        // Try to get the timezone from the country code in this case
                        getTzFromCountryCode: true,
                    },
                })
            );
        }
    };

    return { onIpGeolocResolutionInLegacyCode };
};
